import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/cn";
import { Chip } from "./Chip";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  options: SelectOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  id?: string;
  className?: string;
}

const triggerCls =
  "w-full flex items-center gap-2 bg-surface-2 border border-border rounded-[11px] px-[15px] py-[13px] text-[14.5px] outline-none transition-[border-color,box-shadow] duration-150 focus:border-cyan/60 focus:shadow-[0_0_0_3px_rgba(39,182,203,.14)]";

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  multiple,
  placeholder = "Select…",
  disabled,
  error,
  id,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = multiple ? (value as string[]) : value ? [value as string] : [];

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => { if (open) setActive(0); }, [open]);

  const commit = (optValue: string) => {
    if (multiple) {
      const set = new Set(value as string[]);
      set.has(optValue) ? set.delete(optValue) : set.add(optValue);
      onChange(Array.from(set));
    } else {
      onChange(optValue);
      setOpen(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "Escape") { if (open) { e.stopPropagation(); setOpen(false); } return; }
    if (e.key === "ArrowDown") { e.preventDefault(); if (!open) { setOpen(true); return; } setActive((a) => Math.min(a + 1, options.length - 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); if (!open) { setOpen(true); return; } setActive((a) => Math.max(a - 1, 0)); return; }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      const opt = options[active];
      if (opt) commit(opt.value);
      return;
    }
  };

  const singleLabel = !multiple && value ? options.find((o) => o.value === value)?.label : undefined;

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <div
        role="button"
        id={id}
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        className={cn(
          triggerCls,
          error && "border-danger focus:border-danger",
          disabled ? "opacity-55 cursor-default" : "cursor-pointer",
        )}
      >
        <span className="flex flex-1 flex-wrap items-center gap-1.5 min-w-0 text-left">
          {multiple && selected.length > 0 ? (
            selected.map((v) => (
              <Chip
                key={v}
                size="sm"
                label={options.find((o) => o.value === v)?.label ?? v}
                onRemove={() => commit(v)}
              />
            ))
          ) : singleLabel ? (
            <span className="text-body truncate">{singleLabel}</span>
          ) : (
            <span className="text-faint">{placeholder}</span>
          )}
        </span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-muted shrink-0"><polyline points="6 9 12 15 18 9" /></svg>
      </div>

      {open && (
        <ul
          role="listbox"
          className="absolute z-[1402] mt-1 w-full max-h-[240px] overflow-y-auto rounded-[11px] border border-border bg-surface-hi py-1 shadow-[0_20px_60px_-20px_rgba(0,0,0,.8)]"
        >
          {options.map((opt, i) => {
            const isSel = selected.includes(opt.value);
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSel}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); commit(opt.value); }}
                className={cn(
                  "flex items-center justify-between gap-2 px-[15px] py-[10px] text-[14px] cursor-pointer",
                  i === active ? "bg-surface-2 text-white" : "text-body",
                )}
              >
                <span className="truncate">{opt.label}</span>
                {isSel && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-cyan shrink-0"><polyline points="20 6 9 17 4 12" /></svg>
                )}
              </li>
            );
          })}
          {options.length === 0 && <li className="px-[15px] py-[10px] text-[14px] text-faint">No options</li>}
        </ul>
      )}
    </div>
  );
};

export default Select;
